import {
  Layer,
  LayerLayerTypeEnum,
  MapapiApi,
} from '@datum-group/isogd-openapi/lib';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import { CardOptions, APITableLayer } from './types';

export class ConnectedLayer {
  @observable private _layerInfo?: APITableLayer = undefined;
  @observable private _isLoading = false;
  @observable private _error?: string = undefined;
  private _options: CardOptions;
  private _tableName: string;
  private _mapApi: MapapiApi;

  constructor(tableName: string, options: CardOptions, mapApi: MapapiApi) {
    this._mapApi = mapApi;
    this._options = options;
    this._tableName = tableName;

    makeObservable(this);
  }

  @computed get layerName() {
    return this._layerInfo?.layer_name || this._options.connectedLayer;
  }

  @computed get description() {
    return this._layerInfo?.description;
  }

  @computed get isLoading() {
    return this._isLoading;
  }

  @computed get error() {
    return this._error;
  }

  @computed get layer(): APITableLayer | undefined {
    return this._layerInfo
      ? JSON.parse(JSON.stringify(this._layerInfo))
      : undefined;
  }

  load = async () => {
    const { connectedLayer, findConnectedLayer } = this._options;

    if (connectedLayer) {
      await this._loadLayer(connectedLayer);

      return;
    }

    if (findConnectedLayer) await this._findLayer(this._tableName);
  };

  @action private async _loadLayer(layerName: string) {
    this._isLoading = true;
    this._error = undefined;

    try {
      const resp = await this._mapApi.apiLayersRead(layerName, {
        query: { data_source: this._tableName },
      });

      const layer = resp.data;

      if (!this._isTableLayer(layer)) {
        throw new Error(`Источником для слоя должна быть таблица`);
      }

      runInAction(() => {
        this._layerInfo = layer;
      });
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Не удалось применить фильтры';

      runInAction(() => (this._error = message));
    } finally {
      runInAction(() => (this._isLoading = false));
    }
  }

  @action private async _findLayer(tableName: string) {
    this._isLoading = true;
    this._error = undefined;

    try {
      const resp = await this._mapApi.apiLayersList(1, 0, {
        query: { data_source: tableName },
      });
      const layer = resp.data.results[0];

      if (!layer) throw new Error(`Не найден слой для таблицы ${tableName}`);

      if (!this._isTableLayer(layer)) {
        throw new Error(`Источником для слоя должна быть таблица`);
      }

      runInAction(() => {
        this._layerInfo = layer;
      });
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Не удалось применить фильтры';

      runInAction(() => (this._error = message));
    } finally {
      runInAction(() => (this._isLoading = false));
    }
  }

  private _isTableLayer(layer: Layer): layer is APITableLayer {
    return !!(
      layer.data_source && layer.layer_type === LayerLayerTypeEnum.Table
    );
  }
}
